/**
 * /academy/home for the IELTS General Training track (D-105): the next thing to do (placement,
 * resume, next lesson, review of the day, mock exam), the four skills as activity rings in bands,
 * the estimated overall band and its CLB equivalent, the goal with its countdown, urgent mock
 * exams, practice and history. Following the IELTS for the first time asks for the goal band.
 */

import {
  ApiError,
  type Attempt,
  type AttemptModeEnum,
  type Learner,
  type SkillsEnum,
  getAcademyAttemptsListQueryKey,
  getAcademyMeRetrieveQueryKey,
  getAcademyUrgentListQueryKey,
  useAcademyAttemptsCreate,
  useAcademyAttemptsList,
  useAcademyMeExamsCreate,
  useAcademyMeRetrieve,
  useAcademyProgressRetrieve,
} from "@agencyos/api-client";
import { type ExamProfile, findLesson, ieltsNextStep, lowestClb, parseIeltsProgress, parseIeltsResults, parseProfiles, pick } from "@agencyos/core";
import { Button, GlassCard, SegmentedControl, SelectField, Skeleton, TextField, buttonClass } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  BookOpenCheck,
  CalendarDays,
  ChevronRight,
  ClipboardList,
  Compass,
  Gauge,
  MapPin,
  PencilLine,
  Play,
  Repeat,
  RotateCcw,
  Route,
  Sparkles,
  Target,
  Trophy,
} from "lucide-react";
import { type FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { bandText } from "./band";
import { fieldErrors } from "../../lib/settings";
import { dateText, daysUntil } from "../shared";
import { SKILLS, type Skill, isSkill } from "../types";
import { ActivityRings, CardTitle, SectionTitle, SkillIcon } from "../ui";
import { HomeExtras } from "../coaching/HomeExtras";
import { RenewalBanner } from "../sales/RenewalBanner";
import { UrgentMocks } from "../UrgentMocks";
import { ExamDatesCard } from "../ExamDatesCard";
import { MethodHomeEntry } from "./CourseEntries";
import { useCoursePath, useIeltsFormat } from "./CoursePage";
import { IELTS } from "./exam";
import { MotherTonguePrompt } from "./MotherTongue";

const BAND_TARGETS = [4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9];
const DEFAULT_TARGET = 6.5;

export function useIeltsProfile(learner: Learner | undefined): ExamProfile | null {
  return useMemo(() => parseProfiles(learner?.exam_profiles).find((p) => p.exam === IELTS) ?? null, [learner]);
}

export function followsIelts(learner: Learner | undefined): boolean {
  const exams = Array.isArray(learner?.exams) ? learner.exams : [];
  return exams.includes(IELTS) || parseProfiles(learner?.exam_profiles).some((p) => p.exam === IELTS);
}

/** Goal of the IELTS track (band, date, centre), saved with POST academy/me/exams. */
export function IeltsGoalForm({ profile, onDone, first = false }: { profile: ExamProfile | null; onDone?: () => void; first?: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const save = useAcademyMeExamsCreate();
  const format = useIeltsFormat();
  const [target, setTarget] = useState(String(profile?.target ?? DEFAULT_TARGET));
  const [examDate, setExamDate] = useState(profile?.examDate ?? "");
  const [center, setCenter] = useState(profile?.examCenter ?? "");
  const errors = fieldErrors(save.error);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(
      { data: { exam: IELTS, target: Number(target), exam_date: examDate || null, exam_center: center } },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: getAcademyMeRetrieveQueryKey() });
          void queryClient.invalidateQueries({ queryKey: getAcademyUrgentListQueryKey({ exam: IELTS }) });
          onDone?.();
        },
      },
    );
  };
  const centers = format.centers;
  return (
    <GlassCard as="section" className="ac-card ac-card--accent" aria-labelledby="ielts-goals">
      <form className="ac-form" onSubmit={onSubmit}>
        <CardTitle id="ielts-goals" icon={<Target size={16} />} tone="warning">
          {first ? t("ielts.goal.followTitle") : t("ielts.goal.title")}
        </CardTitle>
        <p className="ac-secondary">{first ? t("ielts.goal.followHint") : t("ielts.goal.hint")}</p>
        <div className="ac-form-grid">
          <SelectField
            label={t("ielts.goal.target")}
            hint={t("ielts.goal.targetHint")}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            options={BAND_TARGETS.map((n) => ({ value: String(n), label: t("ielts.goal.band", { band: bandText(n) }) }))}
            error={errors.target}
          />
          <TextField label={t("academy.goals.examDate")} type="date" value={examDate} onChange={(e) => setExamDate(e.target.value)} error={errors.exam_date} />
          <SelectField
            label={t("ielts.goal.center")}
            value={center}
            onChange={(e) => setCenter(e.target.value)}
            options={[
              { value: "", label: t("academy.goals.choose") },
              ...centers.map((c) => ({ value: c.city, label: c.name ? `${c.city} · ${c.name}` : c.city })),
              ...(center && !centers.some((c) => c.city === center) ? [{ value: center, label: center }] : []),
            ]}
            error={errors.exam_center}
          />
        </div>
        {save.isError && !Object.keys(errors).length && <p className="ko">{t("common.error")}</p>}
        <div className="ac-card__actions">
          {onDone && !first && (
            <Button variant="ghost" onClick={onDone}>
              {t("common.cancel")}
            </Button>
          )}
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? t("common.loading") : first ? t("ielts.goal.follow") : t("academy.goals.save")}
          </Button>
        </div>
      </form>
    </GlassCard>
  );
}

function useStart() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const create = useAcademyAttemptsCreate();
  const start = (kind: Attempt["kind"], skills: SkillsEnum[], mode: AttemptModeEnum = "exam") =>
    create.mutate(
      { data: { exam: IELTS, kind, skills, mode } },
      {
        onSuccess: (response) => {
          if (response.status !== 201) return;
          void queryClient.invalidateQueries({ queryKey: getAcademyAttemptsListQueryKey({ exam: IELTS }) });
          navigate(`/academy/attempt/${response.data.id}`);
        },
      },
    );
  const error = create.error instanceof ApiError ? (create.error.detail ?? null) : create.error ? "" : null;
  return { start, pending: create.isPending, error };
}

function GoalCard({ profile, onEdit }: { profile: ExamProfile; onEdit: () => void }) {
  const { t, i18n } = useTranslation();
  const days = daysUntil(profile.examDate);
  return (
    <GlassCard as="section" className="ac-card ac-goal" aria-labelledby="ielts-goal-title">
      <div className="ac-card__head">
        <CardTitle id="ielts-goal-title" icon={<Trophy size={16} />} tone="warning">
          {t("academy.ui.home.goalTitle")}
        </CardTitle>
        <Button variant="ghost" size="sm" icon={<PencilLine aria-hidden size={15} />} onClick={onEdit}>
          {t("academy.goals.edit")}
        </Button>
      </div>
      <div className="ac-goal__figures">
        <div className="ac-goal__figure">
          <span className="ac-goal__label">{t("academy.progress.target")}</span>
          <span className="ac-goal__value ao-num">{t("ielts.goal.band", { band: bandText(profile.target ?? DEFAULT_TARGET) })}</span>
        </div>
        <div className="ac-goal__figure">
          <span className="ac-goal__label">{t("academy.ui.home.examIn")}</span>
          <span className="ac-goal__value ao-num">
            {days === null ? "—" : days >= 0 ? t("academy.ui.home.days", { count: days }) : t("academy.ui.home.examPast")}
          </span>
        </div>
      </div>
      <ul className="ac-meta-list">
        <li>
          <CalendarDays aria-hidden size={15} />
          <span>{profile.examDate ? dateText(profile.examDate, i18n.language) : t("academy.progress.notSet")}</span>
        </li>
        <li>
          <MapPin aria-hidden size={15} />
          <span>{profile.examCenter || t("academy.progress.notSet")}</span>
        </li>
      </ul>
    </GlassCard>
  );
}

function BandRingsCard({ bands, target }: { bands: Partial<Record<Skill, number>>; target: number }) {
  const { t } = useTranslation();
  const measured = SKILLS.filter((s) => bands[s] !== undefined);
  return (
    <GlassCard as="section" className="ac-card ac-rings-card" aria-labelledby="ielts-rings-title">
      <div className="ac-card__head">
        <CardTitle id="ielts-rings-title" icon={<Target size={16} />}>
          {t("ielts.home.ringsTitle")}
        </CardTitle>
        <Link to="/academy/progress" className="ac-more">
          {t("academy.ui.home.seeProgress")}
          <ChevronRight aria-hidden size={16} />
        </Link>
      </div>
      <div className={measured.length ? "ac-rings-card__body" : "ac-rings-card__empty"}>
        <ActivityRings rings={SKILLS.map((skill) => ({ skill, ratio: (bands[skill] ?? 0) / target }))} size={measured.length ? 168 : 132} stroke={measured.length ? 14 : 11} />
        {measured.length ? (
          <ul className="ac-ring-legend">
            {SKILLS.map((skill) => (
              <li key={skill} className={`ac-skill--${skill}`}>
                <span className="ac-ring-legend__swatch" aria-hidden="true" />
                <span className="ac-ring-legend__name">{t(`ielts.skill.${skill}`)}</span>
                <span className="ac-ring-legend__value ao-num">
                  {bands[skill] !== undefined ? t("ielts.home.ringValue", { band: bandText(bands[skill]), target: bandText(target) }) : t("academy.progress.notMeasured")}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="ac-secondary">{t("ielts.home.ringsEmpty")}</p>
        )}
      </div>
      <p className="ac-footnote">{t("ielts.home.ringsHint", { band: bandText(target) })}</p>
    </GlassCard>
  );
}

function OverallSummary({
  overall,
  clb,
  xp,
  completed,
  total,
  placement,
}: {
  overall: number | null;
  clb: number | null;
  xp: number;
  completed: number;
  total: number;
  placement: ExamProfile | null;
}) {
  const { t } = useTranslation();
  return (
    <GlassCard as="section" className="ac-card ac-summary" aria-labelledby="ielts-summary">
      <CardTitle id="ielts-summary" icon={<Gauge size={16} />} tone="info">
        {t("ielts.home.summaryTitle")}
      </CardTitle>
      <div className="ac-summary__grid">
        <div className="ac-stat">
          <span className="ac-pastille ac-tone--accent" aria-hidden="true">
            <Gauge size={16} />
          </span>
          <div className="ac-stat__body">
            <p className="ac-stat__label">{t("ielts.home.overall")}</p>
            <p className="ac-stat__value ao-num">{overall !== null ? bandText(overall) : placement?.placementBand ? `≈ ${bandText(placement.placementBand)}` : "—"}</p>
            <p className="ac-stat__trend">{overall !== null ? t("ielts.home.overallHint") : placement?.placementBand ? t("ielts.home.fromPlacement") : t("ielts.home.overallNone")}</p>
          </div>
        </div>
        <div className="ac-stat">
          <span className="ac-pastille ac-tone--success" aria-hidden="true">
            <BookOpenCheck size={16} />
          </span>
          <div className="ac-stat__body">
            <p className="ac-stat__label">{t("ielts.home.clb")}</p>
            <p className="ac-stat__value ao-num">{clb !== null ? t("ielts.results.clb", { n: clb }) : "—"}</p>
            <p className="ac-stat__trend">{t("ielts.home.clbHint")}</p>
          </div>
        </div>
        <div className="ac-stat">
          <span className="ac-pastille ac-tone--warning" aria-hidden="true">
            <Sparkles size={16} />
          </span>
          <div className="ac-stat__body">
            <p className="ac-stat__label">{t("academy.progress.xp")}</p>
            <p className="ac-stat__value ao-num">{t("academy.progress.xpValue", { xp })}</p>
          </div>
        </div>
        <div className="ac-stat">
          <span className="ac-pastille ac-tone--eo" aria-hidden="true">
            <Route size={16} />
          </span>
          <div className="ac-stat__body">
            <p className="ac-stat__label">{t("ielts.course.progress")}</p>
            <p className="ac-stat__value ao-num">
              {completed}
              <span className="ac-stat__unit"> / {total}</span>
            </p>
          </div>
        </div>
      </div>
      <p className="ac-footnote">{t("ielts.estimate")}</p>
    </GlassCard>
  );
}

export function IeltsHome() {
  const { t, i18n } = useTranslation();
  const me = useAcademyMeRetrieve({ query: { retry: false } });
  const learner = me.data?.status === 200 ? me.data.data : undefined;
  const profile = useIeltsProfile(learner);
  const following = followsIelts(learner) && profile !== null;
  const locked = learner ? learner.access[IELTS] !== true : true;
  const attempts = useAcademyAttemptsList({ exam: IELTS }, { query: { enabled: following } });
  const progress = useAcademyProgressRetrieve({ exam: IELTS }, { query: { retry: false, staleTime: 60_000, enabled: following } });
  const { path } = useCoursePath(following && !locked);
  const { start, pending, error } = useStart();
  const [editGoals, setEditGoals] = useState(false);
  const [practiceSkill, setPracticeSkill] = useState<Skill>("co");
  const [practiceMode, setPracticeMode] = useState<AttemptModeEnum>("training");
  const analysis = useMemo(() => (progress.data?.status === 200 ? parseIeltsProgress(progress.data.data) : null), [progress.data]);

  if (me.isLoading) {
    return (
      <div className="ac-page">
        <Skeleton style={{ height: 64, maxWidth: 360 }} />
        <div className="ac-grid">
          <Skeleton className="ac-span-7" style={{ height: 220 }} />
          <Skeleton className="ac-span-5" style={{ height: 220 }} />
        </div>
      </div>
    );
  }
  if (!learner) {
    return (
      <GlassCard className="ac-card ac-card--center">
        <p className="ko">{t("common.error")}</p>
        <Button variant="secondary" onClick={() => void me.refetch()}>
          {t("common.retry")}
        </Button>
      </GlassCard>
    );
  }
  const firstName = learner.full_name.split(" ")[0] || learner.email;

  if (!following) {
    return (
      <div className="ac-page">
        <header className="ac-head">
          <div className="ac-head__text">
            <p className="ac-eyebrow">{t("ielts.exams.ielts_general.name")}</p>
            <h1 className="ac-head__title">{t("ielts.home.welcome", { name: firstName })}</h1>
            <p className="ac-head__subtitle">{t("ielts.home.welcomeText")}</p>
          </div>
        </header>
        <IeltsGoalForm profile={null} first />
      </div>
    );
  }

  const target = profile?.target ?? DEFAULT_TARGET;
  const rows = attempts.data?.status === 200 ? attempts.data.data : [];
  const placementOpen = rows.find((a) => a.kind === "placement" && a.status === "in_progress");
  const placementDone = rows.find((a) => a.kind === "placement" && a.status === "submitted");
  // D-160: a paused mock exam never hides (nor stands for) the placement test
  const openAttempt = rows.find((a) => a.status === "in_progress" && a.kind !== "placement");
  const hasPlacement = Boolean(profile?.placed) || Boolean(placementDone);
  const nextLesson = path ? findLesson(path, path.nextLesson) : null;
  const step = ieltsNextStep({
    openAttempt: Boolean(openAttempt),
    openPlacement: Boolean(placementOpen),
    placed: hasPlacement,
    hasAccess: !locked,
    reviewsDue: path?.reviewsDue ?? 0,
    nextLesson: nextLesson?.lesson.id,
  });

  const bands: Partial<Record<Skill, number>> = {};
  for (const skill of SKILLS) {
    const band = analysis?.skills[skill].band;
    if (typeof band === "number") bands[skill] = band;
  }
  const clb = analysis ? lowestClb(SKILLS.map((s) => analysis.skills[s].clb)) : null;

  const cta = (to: string, label: string, icon = <ArrowRight aria-hidden size={18} />) => (
    <Link className={`${buttonClass("secondary", { size: "lg" })} ac-hero-card__cta`} to={to}>
      {icon}
      {label}
    </Link>
  );
  const next = (() => {
    if (step === "placement" && placementOpen)
      return { eyebrow: t("ielts.placement.title"), title: t("academy.placement.inProgress"), action: cta(`/academy/attempt/${placementOpen.id}`, t("academy.placement.resume"), <RotateCcw aria-hidden size={18} />) };
    if (step === "resume" && openAttempt)
      return {
        eyebrow: t("academy.ui.home.nextTitle"),
        title: t("academy.ui.home.resumeTitle", { kind: t(`academy.kind.${openAttempt.kind}`) }),
        text: t("academy.player.saved"),
        action: cta(`/academy/attempt/${openAttempt.id}`, t("academy.history.resume"), <RotateCcw aria-hidden size={18} />),
      };
    if (step === "placement")
      return {
        eyebrow: t("academy.ui.home.nextFree"),
        title: t("ielts.placement.title"),
        text: t("ielts.placement.intro"),
        action: (
          <Button size="lg" variant="secondary" className="ac-hero-card__cta" disabled={pending} onClick={() => start("placement", ["co"])}>
            <Compass aria-hidden size={18} />
            {t("academy.placement.start")}
          </Button>
        ),
      };
    if (step === "plan")
      return { eyebrow: t("academy.ui.home.nextTitle"), title: t("ielts.home.unlockTitle"), text: t("ielts.home.unlockText"), action: cta("/academy/plans", t("academy.plans.choose")) };
    if (step === "review" && path)
      return { eyebrow: t("academy.ui.home.nextTitle"), title: t("ielts.review.title"), text: t("ielts.review.due", { count: path.reviewsDue }), action: cta("/academy/course/review", t("ielts.review.start"), <Repeat aria-hidden size={18} />) };
    if (step === "lesson" && nextLesson)
      return {
        eyebrow: t("ielts.home.nextLesson", { code: nextLesson.lesson.code }),
        title: pick(nextLesson.lesson.title, "en"),
        lang: "en",
        text: t("ielts.home.nextLessonText", { unit: pick(nextLesson.unit.title, "en"), count: nextLesson.lesson.exercises }),
        action: cta(`/academy/course/lesson/${nextLesson.lesson.id}`, t("ielts.course.continue"), <Play aria-hidden size={18} />),
      };
    return { eyebrow: t("academy.ui.home.nextTitle"), title: t("academy.ui.home.mockTitle"), text: t("ielts.home.mockText"), action: cta("/academy/mocks", t("academy.ui.home.mockCta"), <Play aria-hidden size={18} />) };
  })();

  return (
    <div className="ac-page ac-ielts-home">
      <header className="ac-head">
        <div className="ac-head__text">
          <p className="ac-eyebrow">
            {t("ielts.exams.ielts_general.name")} · {t("academy.ui.home.today", { date: dateText(new Date().toISOString(), i18n.language) })}
          </p>
          <h1 className="ac-head__title">{t("academy.home.hello", { name: firstName })}</h1>
          <p className="ac-head__subtitle">
            {t("ielts.home.target", { band: bandText(target) })}
            {profile?.examDate ? ` · ${t("academy.home.examOn", { date: dateText(profile.examDate, i18n.language) })}` : ""}
          </p>
        </div>
      </header>

      <RenewalBanner />
      <HomeExtras />
      <MotherTonguePrompt />
      <UrgentMocks locked={locked} />
      <ExamDatesCard exam="ielts_general" />

      <MethodHomeEntry exam={IELTS} />

      {editGoals && <IeltsGoalForm profile={profile} onDone={() => setEditGoals(false)} />}

      <div className="ac-grid">
        <section className="ac-hero-card ac-span-7" aria-labelledby="ielts-next-title">
          <div className="ac-hero-card__glow" aria-hidden="true" />
          <p className="ac-hero-card__eyebrow">{next.eyebrow}</p>
          <h2 id="ielts-next-title" className="ac-hero-card__title" lang={"lang" in next ? next.lang : undefined}>
            {next.title}
          </h2>
          {next.text && <p className="ac-hero-card__text">{next.text}</p>}
          <div className="ac-hero-card__actions">
            {next.action}
            {step === "review" && nextLesson && (
              <Link to={`/academy/course/lesson/${nextLesson.lesson.id}`} className="ac-hero-card__chip">
                <Play aria-hidden size={14} /> {t("ielts.home.nextLesson", { code: nextLesson.lesson.code })}
              </Link>
            )}
          </div>
        </section>
        {!editGoals && profile && (
          <div className="ac-span-5">
            <GoalCard profile={profile} onEdit={() => setEditGoals(true)} />
          </div>
        )}
      </div>

      <div className="ac-grid">
        <div className="ac-span-5">
          <BandRingsCard bands={bands} target={target} />
        </div>
        <div className="ac-span-7">
          <OverallSummary overall={analysis?.overall ?? null} clb={clb} xp={analysis?.xp ?? profile?.xp ?? 0} completed={path?.completed ?? 0} total={path?.total ?? 0} placement={profile} />
        </div>
      </div>

      <section className="ac-section" aria-labelledby="ielts-train">
        <SectionTitle id="ielts-train" action={<Link to="/academy/course" className="ac-more">{t("ielts.home.openPath")}<ChevronRight aria-hidden size={16} /></Link>}>
          {t("academy.train.title")}
        </SectionTitle>
        {locked && (
          <p className="ac-note" role="note">
            {t("ielts.home.unlockText")}
          </p>
        )}
        <div className="ac-grid">
          <GlassCard as="article" className="ac-card ac-train ac-span-6" aria-labelledby="ielts-train-mock">
            <div className="ac-train__icon ac-tone--accent" aria-hidden="true">
              <ClipboardList size={22} strokeWidth={1.8} />
            </div>
            <h3 id="ielts-train-mock" className="ac-title-3">
              {t("ielts.home.mock")}
            </h3>
            <p className="ac-secondary">{t("ielts.home.mockText")}</p>
            <div className="ac-train__skills" aria-hidden="true">
              {SKILLS.map((s) => (
                <SkillIcon key={s} skill={s} size={15} />
              ))}
              <span className="ac-caption">{t("ielts.home.mockLength")}</span>
            </div>
            <div className="ac-card__actions">
              <Link className={buttonClass("ghost")} to="/academy/mocks">
                {t("academy.mocks.seeAll")}
              </Link>
              <Button variant="tinted" disabled={locked || pending} onClick={() => start("mock", [...SKILLS], "exam")}>
                {t("academy.train.startMock")}
              </Button>
            </div>
          </GlassCard>
          <GlassCard as="article" className="ac-card ac-train ac-span-6" aria-labelledby="ielts-train-practice">
            <h3 id="ielts-train-practice" className="ac-title-3">
              {t("academy.train.practice")}
            </h3>
            <fieldset className="ac-skill-picker" disabled={locked}>
              <legend className="ac-label">{t("academy.train.skill")}</legend>
              {SKILLS.map((s) => (
                <label key={s} className={`ac-skill-option ac-skill--${s}${practiceSkill === s ? " ac-skill-option--on" : ""}`}>
                  <input type="radio" name="ielts-practice-skill" value={s} checked={practiceSkill === s} onChange={(e) => isSkill(e.target.value) && setPracticeSkill(e.target.value)} />
                  <SkillIcon skill={s} size={16} />
                  <span>{t(`ielts.skill.${s}`)}</span>
                </label>
              ))}
            </fieldset>
            <SegmentedControl<AttemptModeEnum>
              label={t("academy.train.mode")}
              value={practiceMode}
              options={[
                { value: "training", label: t("academy.mode.training") },
                { value: "exam", label: t("academy.mode.exam") },
              ]}
              onChange={(value) => setPracticeMode(value)}
            />
            <div className="ac-card__actions">
              <Button variant="secondary" disabled={locked || pending} onClick={() => start("practice", [practiceSkill], practiceMode)}>
                {t("academy.train.startPractice")}
              </Button>
            </div>
          </GlassCard>
        </div>
        {error !== null && <p className="ko">{error || t("common.error")}</p>}
      </section>

      <section className="ac-section" aria-labelledby="ielts-history">
        <SectionTitle id="ielts-history">{t("academy.history.title")}</SectionTitle>
        {attempts.isLoading && <Skeleton style={{ height: 72 }} />}
        {attempts.isSuccess && rows.length === 0 && (
          <div className="ac-surface ac-empty">
            <ClipboardList aria-hidden size={22} />
            <p className="ac-secondary">{t("academy.history.empty")}</p>
          </div>
        )}
        {rows.length > 0 && (
          <ul className="ac-list ac-surface">
            {rows.map((attempt) => {
              const skills = Array.isArray(attempt.skills) ? attempt.skills.filter(isSkill) : [];
              const open = attempt.status === "in_progress";
              const lead = skills.length === 1 && skills[0] ? skills[0] : null;
              const res = parseIeltsResults(attempt.results);
              const shown = SKILLS.filter((s) => res.skills[s]?.band !== null && res.skills[s] !== undefined);
              return (
                <li key={attempt.id}>
                  <Link className="ac-list__row" to={open ? `/academy/attempt/${attempt.id}` : `/academy/attempt/${attempt.id}/results`}>
                    {lead ? (
                      <SkillIcon skill={lead} size={16} />
                    ) : (
                      <span className="ac-pastille ac-tone--accent" aria-hidden="true">
                        {attempt.kind === "placement" ? <Compass size={16} /> : <ClipboardList size={16} />}
                      </span>
                    )}
                    <span className="ac-list__body">
                      <span className="ac-list__title">
                        {t(`academy.kind.${attempt.kind}`)}
                        {attempt.kind === "practice" && skills[0] ? ` · ${t(`ielts.skill.${skills[0]}`)}` : ""}
                      </span>
                      <span className="ac-list__meta">
                        {dateText(attempt.submitted_at ?? attempt.created_at, i18n.language)} · {t(`academy.status.${attempt.status}`)}
                      </span>
                      {!open && (shown.length > 0 || res.overall !== null || res.placement) && (
                        <ul className="ac-chips">
                          {res.overall !== null && (
                            <li className="ac-chip">
                              <strong>{t("ielts.results.overallShort")}</strong> {bandText(res.overall)}
                            </li>
                          )}
                          {res.placement && (
                            <li className="ac-chip">
                              <strong>{t(`ielts.levels.${res.placement.level}`, { defaultValue: res.placement.level })}</strong> {bandText(res.placement.band)}
                            </li>
                          )}
                          {shown.map((s) => (
                            <li key={s} className={`ac-chip ac-skill--${s}`}>
                              <strong>{t(`ielts.skillShort.${s}`)}</strong> {bandText(res.skills[s]?.band)}
                            </li>
                          ))}
                        </ul>
                      )}
                    </span>
                    <span className={`ac-list__action${open ? " ac-list__action--open" : ""}`}>
                      {open ? t("academy.history.resume") : t("academy.history.see")}
                      <ChevronRight aria-hidden size={16} />
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
